/* eslint unicorn/prefer-spread:"off" */
import { computed } from '@ember/object';
import { A } from '@ember/array';
import { alias, equal, notEmpty, or, and, gt, bool } from '@ember/object/computed';
import { isEmpty, isPresent } from '@ember/utils';
import { groupBy } from '@square/dashboard-utilities/utils/array';
import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import { isIn, isAny } from '@square/dashboard-utilities/macros';
import Enum from '@square/dashboard-utilities/utils/enum';
import Adjustment, { AdjustmentType } from '@square/dashboard-data/models/squareup/beemo/api/v2/models/adjustment';
import BeemoCart from '@square/dashboard-data/models/squareup/beemo/api/v2/models/beemo-cart';
import OrderSource from '@square/dashboard-data/models/squareup/beemo/api/v2/models/order-source';
import DateTime from '@square/dashboard-data/models/squareup/common/time/date-time';
import Employee from '@square/dashboard-data/models/squareup/beemo/api/v2/models/employee';
import FeatureDetails from '@square/dashboard-data/models/squareup/beemo/api/v2/models/feature-details';
import flatten from '@square/dashboard-utilities/utils/flatten'; // TODO: remove this and switch to native JS array `flat` function (DASHEX-3441)
import InclusionType from '@square/dashboard-data/models/squareup/api/items/fee/inclusion-type';
import Itemization from '@square/dashboard-data/models/squareup/beemo/api/v2/models/itemization';
import ItemizationGroup from '@square/dashboard-data/models/squareup/beemo/api/v2/models/itemization-group';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import Money from '@square/dashboard-money/models/money';
import PayWithSquareEntryMethod from '@square/dashboard-data/models/squareup/common/pay-with-square-entry-method';
import ProcessingFeeAmounts from '@square/dashboard-data/models/squareup/beemo/api/v2/models/processing-fee-amounts';
import Refund from '@square/dashboard-data/models/squareup/beemo/api/v2/models/refund';
import RefundType from '@square/dashboard-data/models/squareup/common/refund-type';
import RefundV3 from '@square/dashboard-data/models/squareup/beemo/api/v2/models/beemo-refund-v3';
import RefundV3Type from '@square/dashboard-data/models/squareup/beemo/api/v2/models/refund-type';
import Tender, { Type } from '@square/dashboard-data/models/squareup/beemo/api/v2/models/tender';
import Amounts from '@square/dashboard-data/models/squareup/beemo/api/v2/models/amounts';
import MoneyNegatorProxy from '@square/dashboard-money/utils/money-negator-proxy';
import { GetBillRefundableStatusResponse } from '@square/dashboard-protos/models/squareup/client/bills/bill-list-service';
import { TenderType } from './tender-type';
export const EventType = new Enum('PAYMENT', 'REFUND', 'REFUND_V3', 'PAYMENT_EXCHANGE', 'REFUND_EXCHANGE');
export const ActivityType = new Enum('BOOKED', 'PENDING', 'FAILED', 'CANCELED', 'VOIDED', 'OPEN', 'ATO_FRAUD');
export const SquareProduct = new Enum('REGISTER', 'CASH', 'MARKET', 'ORDER', 'EXTERNAL_API', 'BILLING', 'APPOINTMENTS', 'STORE', 'INVOICES', 'ONLINE_STORE', 'STARBUCKS', 'WHITE_LABEL', 'PAYROLL', 'CAPITAL', 'TERMINAL', 'EGIFTING', 'REGISTER_HARDWARE', 'BUYER_SUBSCRIPTIONS', 'FNB_KIOSK');
export const SurchargeType = new Enum('UNKNOWN', 'AUTO_GRATUITY', 'CUSTOM', 'CARD_SURCHARGE');
export const TipDistributionMethod = new Enum('UNKNOWN', 'DIRECT', 'POOLED', 'POOLED_BY_WORKDAY', 'POOLED_BY_HOURS');
export const TipPeriod = new Enum('DO_NOT_USE_TIP_PERIOD', 'WORKDAY', 'WORKWEEK');
const SaleAmountsEmployeeAttribution = ProtoBaseModel.extend({
  employee: attr(Employee),
  tipMoney: attr(MoneyProto),
  autoGratuityMoney: attr(MoneyProto),
  totalCommissionMoney: attr(MoneyProto),
  tipDistributionMethod: attr(TipDistributionMethod),
  tipPeriod: attr(TipPeriod),
  timecardToken: attr(String)
});
const Channel = ProtoBaseModel.extend({
  uid: attr(String),
  channelId: attr(String),
  type: attr(String)
});
export const ProcessingMode = new Enum('ONLINE', 'STORE_AND_FORWARD');
export default ProtoBaseModel.extend({
  // we may have multiple transactions for the same payment token (e.g. refunds)
  eventId: attr(String),
  eventType: attr(EventType),
  billToken: attr(String),
  billEventToken: attr(String),
  completedAt: attr(DateTime),
  merchantToken: attr(String),
  billNote: attr(String),
  featureDetails: attr(FeatureDetails),
  activityType: attr(ActivityType),
  returnCart: attr(BeemoCart),
  squareProduct: attr(SquareProduct),
  orderSource: attr(OrderSource),
  processingFeeAmounts: attr(ProcessingFeeAmounts),
  processingMode: attr(ProcessingMode),
  discounts: repeated(Adjustment),
  channels: repeated(Channel),
  // For commissions and tips
  saleAmountsEmployeeAttribution: repeated(SaleAmountsEmployeeAttribution),
  // These are only populated in transactions/show route (so only visible in that route)
  isRefundablePerRefundableStatusApi: attr(Boolean),
  notRefundableLocalizedReason: attr(String),
  notRefundableReasonEnum: attr(GetBillRefundableStatusResponse.BillNotRefundableReason),
  shouldHaveRefundButtonDisabledPerRefundableStatusApi: computed('isRefundablePerRefundableStatusApi', 'notRefundableReasonEnum', 'hasBitcoinPayment', function () {
    // This keeps the Issue Refund button disabled until the server response indicating whether or not the
    // transaction is refundable has returned.
    if (this.isRefundablePerRefundableStatusApi === undefined) {
      return true;
    }
    if (this.hasBitcoinPayment) {
      return true;
    }
    if (this.isRefundablePerRefundableStatusApi) {
      return false;
    }
    if (this.notRefundableReasonEnum === GetBillRefundableStatusResponse.BillNotRefundableReason.ALREADY_FULLY_REFUNDED || this.notRefundableReasonEnum === GetBillRefundableStatusResponse.BillNotRefundableReason.PAYMENT_DISPUTED) {
      return true;
    }
    return false;
  }).readOnly(),
  itemization: repeated(Itemization),
  itemizationGroups: repeated(ItemizationGroup),
  inclusiveTax: repeated(Adjustment),
  additiveTax: repeated(Adjustment),
  surcharges: repeated(Adjustment),
  tender: repeated(Tender),
  refund: repeated(Refund),
  refundV3: repeated(RefundV3),
  voidItemization: repeated(Itemization),
  orderReferenceId: attr(String),
  orderShortReferenceId: attr(String),
  sequentialNumber: attr(String),
  // deprecations that throw errors to save us from ourselves :
  get creator() {
    throw new Error('Transaction creator is deprecated --> use creatorDetails on Tender.');
  },
  get fullRefund() {
    throw new Error('Transaction fullRefund is deprecated --> use Transaction refund.');
  },
  get device() {
    throw new Error('Transaction device is deprecated --> use device on Tender.');
  },
  get totalMoney() {
    throw new Error('Transaction total money is deprecated --> use total collected.');
  },
  get paymentToken() {
    throw new Error("Transaction payment token is deprecated --> use the first tender's payment token");
  },
  // plural aliases, yes please:
  itemizations: alias('itemization'),
  inclusiveTaxes: alias('inclusiveTax'),
  additiveTaxes: alias('additiveTax'),
  refundV3s: alias('refundV3'),
  isFailedOrCanceled: isIn('activityType', [ActivityType.FAILED, ActivityType.CANCELED]),
  isPending: equal('activityType', ActivityType.PENDING),
  isBooked: equal('activityType', ActivityType.BOOKED),
  isAtoFraud: equal('activityType', ActivityType.ATO_FRAUD),
  amounts: computed('netAmounts', 'returnCart.negatedReturnAmounts', function () {
    // Sale amounts are on the top level
    return this.netAmounts || this.get('returnCart.negatedReturnAmounts') || this;
  }),
  filterReturnedTaxes(inclusionType) {
    let result = [];
    if (this.get('returnItemizations.length') > 0) {
      result = result.concat(flatten(this.returnItemizations.mapBy('tax')).filter(tax => tax.get('inclusionType') === inclusionType));
    }
    if (this.get('returnSurcharges.length') > 0) {
      result = result.concat(flatten(this.returnSurcharges.filter(surcharge => surcharge.get('tax') !== undefined).mapBy('tax')).filter(tax => tax.get('inclusionType') === inclusionType));
    }

    // roll-up the taxes
    return Object.values(groupBy(result, 'adjustmentGroup')).reduce((newResultArr, taxGroup) => {
      if (taxGroup.get('length') > 1) {
        const newAmount = taxGroup.reduce((sum, taxItem) => {
          return sum.add(taxItem.get('appliedMoney'));
        }, Money.zero(taxGroup.get('firstObject.appliedMoney.currencyCode')));
        const mutableTax = taxGroup.get('firstObject.content');
        mutableTax.set('appliedMoney', newAmount);
        newResultArr.pushObject(mutableTax);
      } else {
        newResultArr.pushObject(taxGroup.get('firstObject'));
      }
      return newResultArr;
    }, []);
  },
  combinedAdditiveTaxes: computed('returnItemizations.[]', 'returnSurcharges.[]', 'additiveTaxes', function () {
    const returnedTaxes = this.filterReturnedTaxes(InclusionType.ADDITIVE);
    return A((this.additiveTaxes ? this.additiveTaxes.toArray() : []).concat(returnedTaxes));
  }),
  combinedInclusiveTaxes: computed('returnItemizations.[]', 'returnSurcharges.[]', 'inclusiveTaxes', function () {
    const returnedTaxes = this.filterReturnedTaxes(InclusionType.INCLUSIVE);
    return A((this.inclusiveTaxes ? this.inclusiveTaxes.toArray() : []).concat(returnedTaxes));
  }),
  grossSalesMoney: attr(MoneyProto),
  discountMoney: attr(MoneyProto),
  swedishRoundingMoney: attr(MoneyProto),
  surchargeMoney: attr(MoneyProto),
  surchargeInclusiveTaxMoney: attr(MoneyProto),
  netSalesMoney: attr(MoneyProto),
  giftCardNetSalesMoney: attr(MoneyProto),
  inclusiveTaxMoney: attr(MoneyProto),
  additiveTaxMoney: attr(MoneyProto),
  taxMoney: attr(MoneyProto),
  tipMoney: attr(MoneyProto),
  totalCollectedMoney: attr(MoneyProto),
  totalDepositedMoney: attr(MoneyProto),
  totalDepositedMoneyAfterFreeProcessing: attr(MoneyProto),
  processingFeeMoney: attr(MoneyProto),
  netTotalMoney: attr(MoneyProto),
  refundedMoney: attr(MoneyProto),
  netAmounts: attr(Amounts),
  isMarketOrder: equal('tenders.firstObject.entryMethod', PayWithSquareEntryMethod.WEB_STORE_WEB_FORM),
  isInvoice: notEmpty('featureDetails.invoice'),
  isWalletPickupOrder: equal('tenders.firstObject.entryMethod', PayWithSquareEntryMethod.WALLET_PICKUP),
  isWebPickupOrder: equal('tenders.firstObject.entryMethod', PayWithSquareEntryMethod.WEB_PICKUP),
  isPickupOrder: or('isWebPickupOrder', 'isWalletPickupOrder'),
  isLegacyRefundEvent: equal('eventType', EventType.REFUND),
  isRefundV3Event: notEmpty('refundV3s'),
  isPaymentEvent: isIn('eventType', [EventType.PAYMENT, EventType.PAYMENT_EXCHANGE]),
  isRefundEvent: isIn('eventType', [EventType.REFUND, EventType.REFUND_V3, EventType.REFUND_EXCHANGE]),
  isExchangeEvent: isIn('eventType', [EventType.PAYMENT_EXCHANGE, EventType.REFUND_EXCHANGE]),
  isEvenExchange: and('isExchangeEvent', 'netAmounts.netTotalMoney.isZero'),
  isNoSale: equal('tenders.firstObject.type', Type.NO_SALE),
  isOpen: equal('activityType', ActivityType.OPEN),
  isAmountBasedRefund: computed('isRefundV3Event', 'returnCart.returnLineItems.firstObject.returnLineItemType', function () {
    return this.isRefundV3Event && this.get('returnCart.returnLineItems.firstObject.returnLineItemType') === 'AMOUNT_BASED';
  }),
  isUnlinkedRefund: computed('isRefundV3Event', 'isRefundEvent', 'refunds.firstObject.refundType', function () {
    return (this.isRefundV3Event || this.isRefundEvent) && this.get('refunds.firstObject.refundType') === RefundV3Type.UNLINKED;
  }),
  hasCombos: computed('discounts', function () {
    return this.discounts?.some(discount => discount.type === AdjustmentType.COMBO) || false;
  }),
  hasProcessingFees: isAny('tenders', 'hasProcessingFee'),
  shouldDisplayAsVoided: computed('activityType', 'orderSource.clientOu', 'tenders.{length,firstObject.isCash,firstObject.totalMoney.amount}', function () {
    const activityTypeIsVoided = this.activityType === ActivityType.VOIDED;
    if (this.orderSource?.clientOu !== 'appointments') {
      return activityTypeIsVoided;
    }
    // There are two cases where we should display voids for orders originating from Appointments:
    // 1. Transaction has no tenders
    // 2. Transaction has exactly 1 tender that is a $0 cash/zero_amount payment
    // otherwise there is some payment information to show.
    const tenderCount = this.tenders?.length;
    const hasNoTenders = !tenderCount;
    const hasSingleZeroCashTender = tenderCount === 1 && this.tenders?.firstObject.isCash && this.tenders?.firstObject.totalMoney.amount === 0;
    return activityTypeIsVoided && (hasNoTenders || hasSingleZeroCashTender);
  }),
  isPartialRefund: computed('isRefundEvent', 'refunds.firstObject.type', function () {
    if (this.isRefundEvent) {
      return this.get('refunds.firstObject.type') === RefundType.PARTIAL_REFUND;
    }
    return false;
  }),
  // this is populated in transaction_controller
  // note(seung): this can NOT be an attr otherwise when transitioning to a show route it gets overridden
  hasChargeback: false,
  // DEPRECATED. DO NOT USE THIS PROPERTY.
  // note(seung): normally `tenders` should be a plural alias for the model's `tender` field.
  // I am very saddened these 2 properties are concatenated. For example, a refund exchange should never have tenders.
  // Either use `tender` or `refundV3.@each.linkedTender` explicitly instead.
  tenders: computed('tender', 'refundV3.@each.linkedTender', function () {
    return A((this.tender === undefined ? [] : this.tender.toArray()).concat((this.refundV3 === undefined ? [] : this.refundV3.toArray()).map(r => r.linkedTender)).filter(Boolean) // this is a temporary fix for unlinked refunds, remove when [REFUNDAPI-178] is merged
    );
  }),
  refundTenders: computed('tenders.[]', 'refunds.[]', 'refundV3s.[]', 'isRefundV3Event', 'isUnlinkedRefund', function () {
    if (this.isRefundV3Event) {
      return this.refundV3s.map(refundV3 => {
        if (this.isUnlinkedRefund) {
          if (refundV3.get('destination.giftCardDestination')) {
            return Tender.create({
              type: Type.SQUARE_GIFT_CARD,
              cardBrand: refundV3.get('destination.giftCardDestination.cardTender.card.brand'),
              paymentToken: refundV3.get('paymentToken'),
              giftCardToken: refundV3.get('destination.giftCardDestination.giftCardToken'),
              panSuffix: refundV3.get('destination.giftCardDestination.giftCardPanSuffix')
            });
          }
          const destination = refundV3?.get('destination.tenderDestination');
          const type = destination?.get('type');
          switch (type) {
            case TenderType.CARD:
              {
                const cardTender = destination.get('method.cardTender');
                // Make tender a type that fits beemo tender model
                return Tender.create({
                  type: Type.CREDIT_CARD,
                  cardBrand: cardTender?.card?.brand,
                  panSuffix: cardTender?.card?.panSuffix,
                  entryMethod: cardTender?.card?.entryMethod
                });
              }
            default:
              return Tender.create({
                type
              });
          }
        } else if (isEmpty(refundV3.get('destination.giftCardDestination'))) {
          return refundV3.get('linkedTender');
        } else {
          // handle refund to gift card
          // create a new tender object that represents the refund to gift card
          return Tender.create({
            type: Type.SQUARE_GIFT_CARD,
            cardBrand: 'SQUARE_GIFT_CARD_V2',
            paymentToken: refundV3.get('paymentToken'),
            giftCardToken: refundV3.get('destination.giftCardDestination.giftCardToken'),
            panSuffix: refundV3.get('destination.giftCardDestination.giftCardPanSuffix')
          });
        }
      });
    }
    return this.tenders.filter(tender => isPresent(this.refunds.findBy('paymentToken', tender.get('paymentToken'))));
  }),
  hasCreditTenders: computed('tenders.@each.isCreditCard', function () {
    return (this.tenders === undefined ? A() : this.tenders).isAny('isCreditCard');
  }),
  hasCashTenders: computed('tenders.@each.isCash', function () {
    return (this.tenders === undefined ? A() : this.tenders).isAny('isCash');
  }),
  hasPayWithCashTenders: computed('tender.@each.isPayWithCash', function () {
    return (this.tender === undefined ? A() : this.tender).isAny('isPayWithCash');
  }),
  hasInclusiveTaxes: gt('combinedInclusiveTaxes.length', 0),
  hasSurcharges: or('surcharges.length', 'returnSurcharges.length'),
  surchargeTotalMoney: computed('surchargeMoney', 'surchargeInclusiveTaxMoney', function () {
    // Note: surchargeMoney does not include inclusive surcharge tax, but inclusiveTaxMoney does
    // Since surcharge is shown with the included surcharge inclusive tax, we add it here
    let surchargeTotal = this.surchargeMoney;
    if (this.surchargeInclusiveTaxMoney) {
      surchargeTotal = surchargeTotal.add(this.surchargeInclusiveTaxMoney);
    }
    return surchargeTotal;
  }),
  // DEPRECATED. DO NOT USE THIS PROPERTY.
  // note(seung): normally `refunds` should be a plural alias for the model's `refund`.
  // I am very saddened these 2 properties are concatenated especially because they are different protos.
  // Either use `refund` or `refundV3` explicitly.
  refunds: computed('refund', 'refundV3', function () {
    return A((this.refund === undefined ? [] : this.refund.toArray()).concat(this.refundV3 === undefined ? [] : this.refundV3.toArray()));
  }),
  uniqueSettlementTokens: computed('tenders.[]', function () {
    const tenders = this.tenders;
    if (!tenders) {
      return A();
    }
    return tenders.mapBy('settlementDetails.settlementToken').compact().uniq();
  }),
  uniqueRefundSettlementTokens: computed('refundV3.[]', function () {
    const refunds = this.refundV3;
    if (!refunds) {
      return A();
    }
    return refunds.mapBy('settlementDetails.settlementToken').compact().uniq();
  }),
  returnItemizations: computed('returnCart.returnLineItems.@each.returnItemizations', function () {
    return flatten((this.get('returnCart.returnLineItems') === undefined ? A() : this.get('returnCart.returnLineItems')).mapBy('returnItemizations')).map(returnItemization => returnItemization.get('negatedItemization'));
  }),
  voidItemizations: computed('voidItemization.[]', function () {
    (this.voidItemization === undefined ? A() : this.voidItemization).setEach('isVoid', true);
    return this.voidItemization === undefined ? A() : this.voidItemization;
  }),
  saleAndReturnItemizations: computed('itemizations.[]', 'returnItemizations.[]', function () {
    return A((this.itemizations ? this.itemizations.toArray() : []).concat(this.returnItemizations || []));
  }),
  allItemizations: computed('itemizations.[]', 'returnItemizations.[]', 'voidItemizations.[]', function () {
    return (this.itemizations ? this.itemizations.toArray() : []).concat(this.returnItemizations || []).concat(this.voidItemizations || []);
  }),
  returnSurcharges: computed('returnCart.returnLineItems.@each.returnSurcharges', function () {
    return flatten((this.get('returnCart.returnLineItems') === undefined ? A() : this.get('returnCart.returnLineItems')).mapBy('returnSurcharges')).map(returnSurcharge => MoneyNegatorProxy.create({
      toNegate: returnSurcharge
    }));
  }),
  customTaxedSurcharges: computed('surcharges.[]', 'returnSurcharges.[]', function () {
    return this.filterSurcharges(SurchargeType.CUSTOM).filter(s => isPresent(s.get('tax')));
  }),
  customNonTaxedSurcharges: computed('surcharges.[]', 'returnSurcharges.[]', function () {
    return this.filterSurcharges(SurchargeType.CUSTOM).filter(s => isEmpty(s.get('tax')));
  }),
  cardSurcharges: computed('surcharges.[]', 'returnSurcharges.[]', function () {
    return this.filterSurcharges(SurchargeType.CARD_SURCHARGE);
  }),
  autoGratuitySurcharge: computed('surcharges.[]', 'returnSurcharges.[]', function () {
    return this.filterSurcharges(SurchargeType.AUTO_GRATUITY);
  }),
  filterSurcharges(surchargeType) {
    const results = A();
    results.addObjects(this.surcharges || A());
    results.addObjects(this.returnSurcharges || A());
    return results.filterBy('surchargeType', surchargeType);
  },
  allPaymentTokens: computed('tenders.@each.paymentToken', function () {
    return (this.tenders === undefined ? A() : this.tenders).mapBy('paymentToken');
  }),
  priceMoney: computed('eventType', 'refundV3s.[]', 'refunds.{@each.refundedMoney,firstObject.refundedMoney}', 'totalCollectedMoney', 'grossSalesMoney', 'orderSource.clientOu', function () {
    switch (this.eventType) {
      case EventType.PAYMENT:
      case EventType.PAYMENT_EXCHANGE:
        if (this.orderSource?.clientOu === 'appointments') {
          // When an order-backed appointment is refunded, the refunds are included
          // in the original transaction as opposed to producing a separate transaction.
          // The transaction's totalCollectedMoney does not take refunds into account,
          // so we have to subtract them ourselves.
          return (this.refundV3s ?? []).reduce(function (total, refund) {
            return total.add(refund.get('refundedMoney'));
          }, this.totalCollectedMoney);
        }
        return this.totalCollectedMoney;
      case EventType.REFUND:
        // NOTE (steckel) copy and pasted this out of transaction_controller.
        // Is refunds.firstObject.refundedMoney the right thing to always do?
        return this.get('refunds.firstObject.refundedMoney');
      case EventType.REFUND_V3:
      case EventType.REFUND_EXCHANGE:
        return this.refundV3s.reduce(function (refundedMoney, refund) {
          return refundedMoney.add(refund.get('refundedMoney'));
        }, Money.zero());
      default:
        throw new Error('Unknown transaction.eventType!!');
    }
  }),
  subtotalMoney: computed('amounts.{giftCardNetSalesMoney,inclusiveTaxMoney,netSalesMoney,surchargeMoney}', 'cardSurcharges', function () {
    let subtotal = this.get('amounts.netSalesMoney').add(this.get('amounts.giftCardNetSalesMoney')).add(this.get('amounts.inclusiveTaxMoney'));
    const surchargeTotal = this.get('amounts.surchargeMoney') ?? Money.zero();
    const cardSurchargesTotal = this.cardSurcharges.reduce((total, surcharge) => {
      return total.add(surcharge.get('appliedMoney'));
    }, Money.zero());
    subtotal = subtotal.subtract(surchargeTotal).add(cardSurchargesTotal);
    return subtotal;
  }),
  creatorStaff: computed('tenders.@each.creatorDetails', 'refundV3s.@each.creatorDetails', 'isRefundV3Event', function () {
    const tenderOrRefund = this.isRefundV3Event ? this.refundV3s.findBy('creatorDetails.mobileStaff') : this.tenders.findBy('creatorDetails.mobileStaff');
    return tenderOrRefund ? tenderOrRefund.get('creatorDetails.mobileStaff') : undefined;
  }),
  creatorEmployee: computed('tenders.@each.creatorDetails', 'refundV3s.@each.creatorDetails', 'isRefundV3Event', function () {
    const tenderOrRefund = this.isRefundV3Event ? this.refundV3s.findBy('creatorDetails.employee') : this.tenders.findBy('creatorDetails.employee');
    return tenderOrRefund ? tenderOrRefund.get('creatorDetails.employee') : undefined;
  }),
  hasItemizations: gt('allItemizations.length', 0),
  // 0 amount is still falsey
  hasTip: bool('amounts.tipMoney.amount'),
  showSubtotal: computed('amounts.{netSalesMoney,totalCollectedMoney}', 'hasItemizations', 'isAmountBasedRefund', function () {
    return !this.isAmountBasedRefund && this.hasItemizations && this.get('amounts.netSalesMoney') && !this.get('amounts.netSalesMoney').equals(this.get('amounts.totalCollectedMoney'));
  }),
  showSubtotalSection: computed('hasSurcharges', 'additiveTaxes.length', 'hasTip', 'showSubtotal', function () {
    return this.hasSurcharges || this.get('additiveTaxes.length') > 0 || this.hasTip || this.showSubtotal;
  }),
  mostSevereRiskEvaluation: computed('tenders.@each.riskEvaluationResult', function () {
    return this.tenders.mapBy('riskEvaluationResult').compact().sortBy('rank').lastObject;
  }),
  bitcoinTotal: computed('tenders.{@each.isBitcoinPayment,@each.buyerCurrencyExchange}', function () {
    const bitcoinTenders = this.tenders.filterBy('isBitcoinPayment');
    if (bitcoinTenders.length === 0) {
      return null;
    }

    // Sum up all the amountMoney values
    let totalAmount = 0;
    let currencyCode = null;
    bitcoinTenders.forEach(tender => {
      const exchange = tender.buyerCurrencyExchange;
      if (exchange && exchange.amountMoney) {
        totalAmount += exchange.amountMoney.amount || 0;
        if (!currencyCode) {
          currencyCode = exchange.amountMoney.currencyCode;
        }
      }
    });
    if (currencyCode && totalAmount > 0) {
      return Money.create({
        amount: totalAmount,
        currencyCode
      });
    }
    return null;
  }),
  bitcoinUsdTotal: computed('tenders.@each.buyerCurrencyExchange', function () {
    let usdTotal = 0;
    this.tenders.filterBy('isBitcoinPayment').forEach(tender => {
      usdTotal += tender.totalMoney.amount;
    });
    return Money.create({
      amount: Math.round(usdTotal),
      currencyCode: 'USD'
    });
  }),
  hasBitcoinPayment: computed('tenders.@each.isBitcoinPayment', function () {
    return this.tenders.isAny('isBitcoinPayment');
  })
}).reopenClass({
  // note(seung): not setting a `primaryKey` fixes a race condition where if the txn show page is visited directly
  // and a customer is loaded on the txn show route and then the txns all page loads behind it later
  // it will override customer data. (ONCALL-44452)
  // primaryKey: 'eventId',

  deserialize(attributes) {
    if (attributes) {
      if (attributes.event_type === EventType.REFUND) {
        // eslint-disable-next-line no-param-reassign
        attributes.event_id = `${attributes.refund[0].payment_token}-${attributes.refund[0].refund_uuid}`;
      } else {
        // eslint-disable-next-line no-param-reassign
        attributes.event_id = attributes.bill_token;
      }
    }
    return this._super(attributes);
  }
});